/**
 * BurnChart — daily credit burn over the last N days.
 *
 *   • Total mode → single bar per day (total credits across every project).
 *   • Per-project mode → stacked bar per day, each segment = one project.
 *
 * Recharts is already in package.json (3.6.0). We keep the component
 * self-contained: fetches its own data, owns its mode toggle, no shared
 * state with the parent table beyond a `reloadKey` prop that lets the ROI
 * page force a refetch after burns are logged.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { AlertTriangle, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";

/** Deterministic palette — each project gets a stable color even after
 *  reloads by hashing its id. Colors chosen for legibility on dark BG. */
const COLORS = [
  "#7C5CFF", "#22C55E", "#F59E0B", "#38BDF8", "#F43F5E",
  "#A855F7", "#10B981", "#EAB308", "#0EA5E9", "#EC4899",
  "#D4AF37", "#06B6D4",
];
const colorFor = (idx) => COLORS[idx % COLORS.length];

const shortDate = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

export default function BurnChart({ reloadKey = 0 }) {
  const [series,   setSeries]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [days,     setDays]     = useState(30);
  const [mode,     setMode]     = useState("total"); // "total" | "stack"
  const [loading,  setLoading]  = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/roi/daily?days=${days}`);
      setSeries(data.series || []);
      setProjects(data.projects || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "Chart load failed");
      }
      setSeries([]); setProjects([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days, reloadKey]);

  // Derived stats — running totals, peak day, alerts (over-threshold days
  // are surfaced from the per-project alert flag in the summary endpoint;
  // here we just compute peak so operators know the worst day.)
  const stats = useMemo(() => {
    if (!series.length) return { total: 0, peak: null, avg: 0 };
    const total = series.reduce((a, b) => a + (b.total || 0), 0);
    const peak = series.reduce((best, cur) => (cur.total > (best?.total || 0) ? cur : best), null);
    return { total, peak, avg: total / series.length };
  }, [series]);

  return (
    <div className="border border-white/10 bg-white/[0.02]" data-testid="roi-burn-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 bg-white/[0.03]">
        <div className="flex items-center gap-3">
          <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-gold uppercase">
            // Daily Burn · Last {days} Days
          </div>
          {loading && <span className="font-mono text-[0.6rem] text-nex-muted">Loading…</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { key: "total", label: "Total" },
            { key: "stack", label: "Per Project" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              data-testid={`roi-chart-mode-${key}`}
              className={`px-2.5 py-1 border font-mono text-[0.55rem] tracking-[0.22em] uppercase transition-all ${
                mode === key
                  ? "border-nex-gold/60 bg-nex-gold/10 text-nex-gold"
                  : "border-white/10 text-nex-chrome hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
          <span className="mx-1 h-4 w-px bg-white/10" />
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`roi-chart-range-${d}`}
              className={`px-2 py-1 border font-mono text-[0.55rem] tracking-[0.22em] uppercase transition-all ${
                days === d
                  ? "border-nex-gold/60 bg-nex-gold/10 text-nex-gold"
                  : "border-white/10 text-nex-chrome hover:border-white/30"
              }`}
            >
              {d}D
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="roi-chart-refresh"
            className="px-2 py-1 border border-white/10 text-nex-chrome hover:border-white/30 hover:text-nex-silver"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-white/5">
        <MiniStat label="Total credits"      value={Math.round(stats.total).toLocaleString()} testId="roi-chart-stat-total" />
        <MiniStat label={`Avg / day`}        value={Math.round(stats.avg).toLocaleString()} testId="roi-chart-stat-avg" />
        <MiniStat label="Peak day"           value={stats.peak ? `${Math.round(stats.peak.total).toLocaleString()}` : "—"} testId="roi-chart-stat-peak" />
        <MiniStat label="Peak on"            value={stats.peak?.date ? shortDate(stats.peak.date) : "—"} icon={TrendingUp} testId="roi-chart-stat-peak-on" />
      </div>

      <div className="p-3" style={{ width: "100%", height: 320 }}>
        {series.length === 0 && !loading ? (
          <div className="w-full h-full flex items-center justify-center font-mono text-[0.65rem] text-nex-muted">
            No burn data yet — log credits or import a CSV in the table below.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                stroke="rgba(226,232,240,0.5)"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
                axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                tickLine={false}
              />
              <YAxis
                stroke="rgba(226,232,240,0.5)"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
                axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
                tickLine={false}
                width={44}
              />
              <Tooltip
                contentStyle={{
                  background: "#0B1220",
                  border: "1px solid rgba(255,255,255,0.15)",
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 11,
                  color: "#E2E8F0",
                }}
                cursor={{ fill: "rgba(212,175,55,0.06)" }}
                labelFormatter={shortDate}
                formatter={(value, name) => {
                  if (name === "total") return [Math.round(value).toLocaleString(), "Total"];
                  const p = projects.find((x) => x.id === name);
                  return [Math.round(value).toLocaleString(), p?.name || name];
                }}
              />
              {mode === "stack" && projects.length > 0 && (
                <Legend
                  wrapperStyle={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, color: "#94A3B8" }}
                  formatter={(name) => projects.find((x) => x.id === name)?.name || name}
                />
              )}

              {mode === "total" ? (
                <Bar dataKey="total" fill="#D4AF37" radius={[2, 2, 0, 0]} maxBarSize={24} />
              ) : (
                projects.map((p, i) => (
                  <Bar key={p.id} dataKey={p.id} stackId="a" fill={colorFor(i)} radius={i === projects.length - 1 ? [2, 2, 0, 0] : 0} maxBarSize={24} />
                ))
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, testId }) {
  return (
    <div className="px-4 py-2 border-r border-white/5 last:border-r-0" data-testid={testId}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3 text-nex-chrome" />}
        <span className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-muted uppercase">{label}</span>
      </div>
      <div className="text-nex-silver font-mono text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
